import {
  Alert,
  Box,
  CheckBox,
  Typography
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useGetFeatureAccess } from "@dealer-center/ddc-pages-configuration-fetch";
import type { MyInfoSubject } from "../../useMyInfoSubject";
import { ReadOnlyNotice } from "../ReadOnlyNotice";
import { SCREEN_READER_ONLY } from "../StatusRegion/screenReaderOnly";
import { TabState } from "../TabState";
import { PermissionGroup } from "./PermissionGroup";
import { groupPermissions } from "./groupPermissions";

export interface FeatureAccessTabProps {
  subject: MyInfoSubject;
}

/**
 * Every feature the account is entitled to, grouped, each marked granted or not granted, read-only.
 *
 * **One query, and it is not the profile query.** `useGetFeatureAccess` supplies both the rows and
 * the `administrator` flag, because CC9 moved that flag onto this response (interface
 * `FeatureAccessResponse` is the single normative statement of its location). Reading it from
 * `useGetUserInfo` is a compile error by design (spec SC-011) — and would silently report every user
 * as not an administrator if the types allowed it. This tab therefore has **no dependency on US1**.
 *
 * `administrator` and the rows are display data, never authorization (Monorepo Principle VII). Real
 * authorization is `StrictlyInternalGuard`, the LD flag, and the lambda's `isInternalUser`.
 */
export const FeatureAccessTab = ({ subject }: FeatureAccessTabProps) => {
  const { data, isPending, isError } = useGetFeatureAccess({
    userId: subject.userId,
    accountId: subject.accountId,
    // `getCurrent()`, not `getLocale()` — see the note in `LoginHistoryTab`.
    locale: Locale.getCurrent()
  });

  const permissions = data?.permissions ?? [];
  const groups = groupPermissions(
    permissions,
    Locale.getText("GROUP_UNGROUPED") as string
  );

  /*
   * FR-035's distinction, and the one most likely to be built wrongly: "no permissions" and "no
   * *granted* permissions" are different states. Empty means **zero rows returned**. A user granted
   * none of the rows returned sees the full not-granted list, which is different and more useful
   * information — so this must be computed from row count, never from a granted count.
   *
   * `administrator` is excluded from "empty" because `TabState` substitutes the empty message for
   * `children` *entirely*. An administrator with zero rows is a real combination, and on row count
   * alone it would render the empty message and drop the administrator entry below — contradicting
   * FR-034's guarantee that the entry renders for both states. The empty message is only correct
   * when there is genuinely nothing to show.
   */
  const isEmpty = permissions.length === 0 && !data?.administrator;

  return (
    <TabState
      emptyMessage={Locale.getText("EMPTY_PERMS") as string}
      isEmpty={isEmpty}
      isError={isError}
      isLoading={isPending}
      loadingActionName={"Loading My Info feature access"}
      testId={"feature-access"}
    >
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
        {/*
         * T068. A heading-level anchor, visually hidden.
         *
         * MUI's `Accordion` wraps each group summary in its own `<h3>` (Phase 4 note 3), and this tab
         * had no `h2` of its own — so the document went `h1` → `h3`, skipping a level. A screen-reader
         * user navigating by heading sees a broken outline, and it is the one WCAG 1.3.1 problem on
         * this page that no amount of per-component correctness fixes, because it only exists in the
         * *combination* of the page's `h1` and MUI's `h3`.
         *
         * Hidden rather than visible because the tab panel is already labelled by its tab, so a
         * visible "Feature Access" heading directly under the "Feature Access" tab would be redundant
         * to a sighted user while adding nothing for anyone. It reuses `FEATURE_ACCESS` — the same
         * copy as the tab — so it introduces no new label key and cannot drift from the tab name.
         *
         * The User Information tab needs no equivalent: it already emits two real `h2` sections.
         * Account Access and Login History emit no headings at all, so they skip nothing.
         */}
        <Typography sx={SCREEN_READER_ONLY} tag={"h2"} variant={"h4"}>
          {Locale.getText("FEATURE_ACCESS") as string}
        </Typography>

        <ReadOnlyNotice />

        {/*
         * FR-034. Rendered from `data.administrator` — the sibling field on this tab's own response,
         * beside `permissions` rather than inside it. It is deliberately **outside** the grouped
         * list: no CC9 feature code exists for it, `groupId` is never `"global"`, and a live search
         * for a row named /admin/i returns nothing. Keeping it out of `groups` is what stops a
         * reader mistaking it for a row now that it arrives on the same response as the rows.
         *
         * The entry renders for `false` as well as `true`, so a non-administrator can see the state
         * rather than infer it from an absence. The note is what FR-034's "confers access beyond the
         * individually-listed permissions" requires, and only applies when the flag is set.
         *
         * `data` is guaranteed here: `TabState` renders children only after a successful, non-empty
         * query. The guard is what narrows the type without a cast — and it also means a *failed*
         * read shows no administrator entry at all, rather than inventing "not an administrator"
         * from an absent response.
         */}
        {data ? (
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "8px" }}>
            <CheckBox
              disabled
              checked={data.administrator}
              data-testid={"administrator-entry"}
              id={"permission-administrator"}
              label={Locale.getText("PERM_ADMINISTRATOR") as string}
              name={"administrator"}
              value={"administrator"}
            />
            {data.administrator ? (
              <Alert
                data-testid={"administrator-note"}
                role={"status"}
                type={"info"}
              >
                {Locale.getText("ADMIN_NOTE") as string}
              </Alert>
            ) : null}
          </Box>
        ) : null}

        {/*
         * Each group owns its own expansion state — see `PermissionGroup` for why Interstate's
         * `AccordionGroup` is deliberately not used here. `map` over an array, never a spread of a
         * Map, so no TS2802 (research R-011).
         */}
        <Box data-testid={"permission-groups"}>
          {groups.map(group => (
            <PermissionGroup group={group} key={group.key} />
          ))}
        </Box>
      </Box>
    </TabState>
  );
};
